import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import useClickOutside from "../../../../hooks/useClickOutside";
import { createStateHash } from "../../utils/keyboardStateUtils";
import { getSupportedImageAcceptString } from "../../utils/imageFormatUtils";
import "../styles.css";

const Toolbar = ({
  action,
  setAction,
  ACTIONS,
  addText,
  deleteSelectedObject,
  fillColor,
  setFillColor,
  handleExport,
  handleImageUpload,
  handleImportCanvas,
  setShowWebcamPopup,
  handleUndo,
  handleRedo,
  clearCanvas,
  handlePaste,
  history,
  redoStack,
  zoomLevel = 1,
  setShowTextBox,
  setSelectedText,
  setSelectedTextId,
  lockedAction,
  setLockedAction,
  images, // Current images state - needed to check if we're at initial with images
  setSelectedObject, // Function to clear selected object (closes ShapeEditor)
  setSelectedObjects, // Function to clear selected objects (closes ShapeEditor)
  isMultiSelectMode = false, // Multi-select mode flag - disables toolbar when enabled
  isPastingFromClipboard = false,
}) => {
  const exportButtonRef = useRef(null);
  const exportMenuRef = useRef(null);
  const importCanvasInputRef = useRef(null);
  const imageUploadInputRef = useRef(null);
  const [exportMenuPosition, setExportMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Close export menu when clicking outside
  useClickOutside(exportMenuRef, () => {
    if (showExportMenu) {
      setShowExportMenu(false);
    }
  });

  useEffect(() => {
    const onCanvasEscape = () => setShowExportMenu(false);
    window.addEventListener("canvasEscape", onCanvasEscape);
    return () => window.removeEventListener("canvasEscape", onCanvasEscape);
  }, []);
  // Tool button configurations rearranged in the specified sequence
  const toolButtons = [
    {
      action: ACTIONS.SELECT,
      imgSrc: "/sending.svg",
      label: "Select (S)",
    },
  ];
  const currentState = history[history.length - 1];

  // Check if next state (for undo) is different from current state
  const nextStateForUndo = history.length > 1 ? history[history.length - 2] : null;

  // CRITICAL: Always allow undo if the last state is delete_object, delete_multiple_objects, cut_image, element_edit_complete, before_drag_transfer, or drag_transfer, even if hash matches
  // This is because delete operations save the state BEFORE deletion, which might have the same hash
  // as the previous state (e.g., add_preset_image), but they represent different operations
  // Similarly, cut_image saves the state BEFORE cutting, which might have the same hash
  // as the previous state, but they represent different operations
  // element_edit_complete is only called when there are pending changes, so it should always be undoable
  // even if the hash matches (e.g., due to hash calculation not including all fields)
  // before_drag_transfer saves the state BEFORE transferring an element between canvases, which might have
  // the same hash as add_preset_image, but they represent different operations
  // drag_transfer saves the state AFTER transferring an element between canvases, which should always be undoable
  const isLastStateDelete =
    currentState?.operationType === "delete_object" ||
    currentState?.operationType === "delete_multiple_objects";
  const isLastStateCut = currentState?.operationType === "cut_image";
  const isLastStateElementEditComplete = currentState?.operationType === "element_edit_complete";
  const isLastStateBeforeDragTransfer = currentState?.operationType === "before_drag_transfer";
  const isLastStateDragTransfer = currentState?.operationType === "drag_transfer";
  const isLastStateCanvasHeightChange = currentState?.operationType === "canvas_height_change";

  const isNextStateDifferent = nextStateForUndo ? (() => {
    try {
      // If last state is delete_object, cut_image, element_edit_complete, before_drag_transfer, drag_transfer, or canvas_height_change, always allow undo (don't check hash)
      if (isLastStateDelete || isLastStateCut || isLastStateElementEditComplete || isLastStateBeforeDragTransfer || isLastStateDragTransfer || isLastStateCanvasHeightChange) {
        return true;
      }
      const currentHash = createStateHash(currentState);
      const nextHash = createStateHash(nextStateForUndo);
      return currentHash !== nextHash;
    } catch (e) {
      // Fallback: assume different if we can't compare
      return true;
    }
  })() : false;

  // Check if next state (for redo) is different from current state
  const nextStateForRedo = redoStack && redoStack.length > 0 ? redoStack[redoStack.length - 1] : null;

  // CRITICAL: Always allow redo if the redo state is delete_object, delete_multiple_objects, cut_image, or add_preset_image, even if hash matches
  // This is because delete operations save the state BEFORE deletion, which might have the same hash
  // as the current state (after undo), but they represent different operations
  // Similarly, cut_image saves the state BEFORE cutting, which might have the same hash
  // as the current state (after undo), but they represent different operations
  // add_preset_image should always be redoable, even if hash matches
  const isRedoStateDelete =
    nextStateForRedo?.operationType === "delete_object" ||
    nextStateForRedo?.operationType === "delete_multiple_objects";
  const isRedoStateCut = nextStateForRedo?.operationType === "cut_image";
  const isRedoStateAddImage = nextStateForRedo?.operationType === "add_preset_image";
  const isRedoStateCanvasHeightChange = nextStateForRedo?.operationType === "canvas_height_change";

  const isRedoStateDifferent = nextStateForRedo ? (() => {
    try {
      // If redo state is delete_object, cut_image, add_preset_image, or canvas_height_change, always allow redo (don't check hash)
      if (isRedoStateDelete || isRedoStateCut || isRedoStateAddImage || isRedoStateCanvasHeightChange) {
        return true;
      }
      const currentHash = createStateHash(currentState);
      const redoHash = createStateHash(nextStateForRedo);
      return currentHash !== redoHash;
    } catch (e) {
      // Fallback: assume different if we can't compare
      return true;
    }
  })() : false;

  // When history.length === 1 we're at initial state - disable undo so it matches off-canvas and main canvas
  // after undoing back to initial. (Removed isAtInitialWithImages so undo never stays enabled at initial.)
  const canUndo = history.length > 1 && isNextStateDifferent;
  const canRedo = redoStack && redoStack.length > 0 && isRedoStateDifferent; // Need redo stack and states must be different
  const isUndoDisabled = !canUndo;
  const isRedoDisabled = !canRedo;

  const lineToolIconColor =
    lockedAction === ACTIONS.LINE || action === ACTIONS.LINE
      ? "white"
      : "#6C757D";

  const getUnifiedLineArrowDefaultPresetHeadFlags = () => {
    // Use the same storage key as UnifiedLineArrowEditor default preset.
    const fallback = { hasStartArrow: false, hasEndArrow: true };
    if (typeof window === "undefined") return fallback;

    try {
      const rawPreset = window.localStorage.getItem(
        "unifiedLineArrowDefaultStylePreset",
      );
      if (!rawPreset) return fallback;

      const preset = JSON.parse(rawPreset);
      if (!preset || typeof preset !== "object") return fallback;

      const hasStartArrow =
        preset.hasStartArrow === true || preset.pointerAtBeginning === true;
      const hasEndArrow =
        preset.hasEndArrow === true ||
        preset.pointerAtEnd === true ||
        preset.pointerAtEnding === true;

      return { hasStartArrow, hasEndArrow };
    } catch (_error) {
      return fallback;
    }
  };

  const [defaultPresetHeadFlags, setDefaultPresetHeadFlags] = useState(
    getUnifiedLineArrowDefaultPresetHeadFlags,
  );

  useEffect(() => {
    const syncFromStorage = () => {
      setDefaultPresetHeadFlags(getUnifiedLineArrowDefaultPresetHeadFlags());
    };

    const handleDefaultPresetChanged = (event) => {
      const hasDetail =
        event?.detail &&
        typeof event.detail.hasStartArrow === "boolean" &&
        typeof event.detail.hasEndArrow === "boolean";

      if (hasDetail) {
        setDefaultPresetHeadFlags({
          hasStartArrow: event.detail.hasStartArrow,
          hasEndArrow: event.detail.hasEndArrow,
        });
        return;
      }

      syncFromStorage();
    };

    window.addEventListener("storage", syncFromStorage);
    window.addEventListener(
      "unifiedLineArrowDefaultPresetChanged",
      handleDefaultPresetChanged,
    );

    return () => {
      window.removeEventListener("storage", syncFromStorage);
      window.removeEventListener(
        "unifiedLineArrowDefaultPresetChanged",
        handleDefaultPresetChanged,
      );
    };
  }, []);

  const { hasStartArrow: defaultHasStartArrow, hasEndArrow: defaultHasEndArrow } =
    defaultPresetHeadFlags;
  const isDefaultDoubleHeadLine = defaultHasStartArrow && defaultHasEndArrow;
  const isDefaultNoHeadLine = !defaultHasStartArrow && !defaultHasEndArrow;

  return (
    <div
      className="toolbar-wrapper"
      data-block-leave-canvas
      onMouseDownCapture={() => {
        // Clear any current selection when interacting with the toolbar.
        // This prevents selection-layer rendering rules from temporarily hiding
        // newly created elements while they become selected.
        if (typeof setSelectedObject === "function") setSelectedObject(null);
        if (typeof setSelectedObjects === "function") setSelectedObjects([]);
      }}
      onTouchStartCapture={() => {
        if (typeof setSelectedObject === "function") setSelectedObject(null);
        if (typeof setSelectedObjects === "function") setSelectedObjects([]);
      }}
      style={{
        transform: `translateY(-50%)`,
        transformOrigin: "left center",
      }}
    >
      {/* Pan button */}
      <button
        className={`toolbar-btn ${action === ACTIONS.PAN ? "active-tool" : ""}`}
        onClick={() => {
          if (isMultiSelectMode) return; // Disable during multi-select
          setLockedAction(null);
          setAction(ACTIONS.PAN);
          setShowExportMenu(false);
          if (setShowTextBox) setShowTextBox(false);
          if (setSelectedText) setSelectedText(null);
          if (setSelectedTextId) setSelectedTextId(null);
        }}
        disabled={isMultiSelectMode}
        style={{
          backgroundColor: action === ACTIONS.PAN ? "#007bff" : "transparent",
          color: "white",
          width: "25px",
          height: "25px",
          borderRadius: "2px",
          border: "none",
          fontSize: "15px",
          cursor: isMultiSelectMode ? "not-allowed" : "pointer",
          opacity: isMultiSelectMode ? 0.4 : 1,
        }}
      >
        <svg
          width="18"
          height="19"
          viewBox="0 0 18 19"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.04545 19C7.59091 19 7.16667 18.9028 6.77273 18.7085C6.37879 18.5142 6.05303 18.2371 5.79545 17.8773L0 9.82386L0.545455 9.32727C0.833333 9.05379 1.17424 8.89545 1.56818 8.85227C1.96212 8.80909 2.31818 8.89545 2.63636 9.11136L5.27273 10.8602V2.59091C5.27273 2.34621 5.35985 2.1411 5.53409 1.97557C5.70833 1.81004 5.92424 1.72727 6.18182 1.72727C6.43939 1.72727 6.6553 1.81004 6.82955 1.97557C7.00379 2.1411 7.09091 2.34621 7.09091 2.59091V14.1852L3.72727 11.9398L7.29545 16.8841C7.37121 16.9992 7.47727 17.0928 7.61364 17.1648C7.75 17.2367 7.89394 17.2727 8.04545 17.2727H14.3636C14.8636 17.2727 15.2917 17.1036 15.6477 16.7653C16.0038 16.4271 16.1818 16.0205 16.1818 15.5455V3.45455C16.1818 3.20985 16.2689 3.00473 16.4432 2.8392C16.6174 2.67367 16.8333 2.59091 17.0909 2.59091C17.3485 2.59091 17.5644 2.67367 17.7386 2.8392C17.9129 3.00473 18 3.20985 18 3.45455V15.5455C18 16.4955 17.6439 17.3087 16.9318 17.9852C16.2197 18.6617 15.3636 19 14.3636 19H8.04545ZM8.90909 9.5V0.863636C8.90909 0.618939 8.99621 0.413826 9.17045 0.248295C9.3447 0.0827652 9.56061 0 9.81818 0C10.0758 0 10.2917 0.0827652 10.4659 0.248295C10.6402 0.413826 10.7273 0.618939 10.7273 0.863636V9.5H8.90909ZM12.5455 9.5V1.72727C12.5455 1.48258 12.6326 1.27746 12.8068 1.11193C12.9811 0.946402 13.197 0.863636 13.4545 0.863636C13.7121 0.863636 13.928 0.946402 14.1023 1.11193C14.2765 1.27746 14.3636 1.48258 14.3636 1.72727V9.5H12.5455Z"
            fill={action === ACTIONS.PAN ? "white" : "#6C757D"}
          />
        </svg>
        <span className="tooltip">Pan (H)</span>
      </button>
      {/* Select button */}
      {toolButtons.map(({ action: act, label }) => {
        const isSelected = action === act;
        return (
          <button
            key={act}
            className={`toolbar-btn ${action === act ? "active-tool" : ""}`}
            onClick={() => {
              if (isMultiSelectMode) return; // Disable during multi-select
              setLockedAction(null);
              setAction(act);
              setShowExportMenu(false);
              if (setShowTextBox) setShowTextBox(false);
              if (setSelectedText) setSelectedText(null);
              if (setSelectedTextId) setSelectedTextId(null);
            }}
            disabled={isMultiSelectMode}
            style={{
              backgroundColor: isSelected ? "#007bff" : "transparent",
              color: "white",
              width: "25px",
              height: "25px",
              borderRadius: "2px",
              border: "none",
              fontSize: "15px",
              cursor: isMultiSelectMode ? "not-allowed" : "pointer",
              opacity: isMultiSelectMode ? 0.4 : 1,
            }}
          >
            {isSelected ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.8077 0.191447C17.9014 0.285242 17.9645 0.405254 17.9886 0.535642C18.0127 0.66603 17.9967 0.800664 17.9427 0.921774L10.5254 17.6105C10.4736 17.7272 10.3888 17.8262 10.2815 17.8954C10.1741 17.9646 10.0489 18.001 9.92122 18C9.79352 17.999 9.66888 17.9608 9.56262 17.8899C9.45636 17.8191 9.37309 17.7188 9.32306 17.6013L6.64694 11.3522L0.39655 8.67478C0.279494 8.62443 0.179648 8.54105 0.109224 8.43486C0.038799 8.32866 0.000848711 8.20425 1.40694e-05 8.07683C-0.000820572 7.9494 0.0354967 7.8245 0.104524 7.71739C0.173551 7.61028 0.272297 7.52561 0.388683 7.47373L17.0774 0.056396C17.1983 0.00270042 17.3327 -0.0131363 17.4628 0.0109682C17.5929 0.0350728 17.7127 0.09799 17.8064 0.191447H17.8077Z"
                  fill="white"
                />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.8077 0.191447C17.9014 0.285242 17.9645 0.405254 17.9886 0.535642C18.0127 0.66603 17.9967 0.800664 17.9427 0.921774L10.5254 17.6105C10.4736 17.7272 10.3888 17.8262 10.2815 17.8954C10.1741 17.9646 10.0489 18.001 9.92122 18C9.79352 17.999 9.66888 17.9608 9.56262 17.8899C9.45636 17.8191 9.37309 17.7188 9.32306 17.6013L6.64694 11.3522L0.39655 8.67478C0.279494 8.62443 0.179648 8.54105 0.109224 8.43486C0.038799 8.32866 0.000848711 8.20425 1.40694e-05 8.07683C-0.000820572 7.9494 0.0354967 7.8245 0.104524 7.71739C0.173551 7.61028 0.272297 7.52561 0.388683 7.47373L17.0774 0.056396C17.1983 0.00270042 17.3327 -0.0131363 17.4628 0.0109682C17.5929 0.0350728 17.7127 0.09799 17.8064 0.191447H17.8077Z"
                  fill="#6C757D"
                />
              </svg>
            )}
            <span className="tooltip">{label}</span>
          </button>
        );
      })}
      {/* Undo button */}
      <button
        className="toolbar-btn"
        onClick={() => {
          if (!isUndoDisabled && !isMultiSelectMode) {
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
            handleUndo();
            setShowExportMenu(false);
            if (setShowTextBox) setShowTextBox(false);
            if (setSelectedText) setSelectedText(null);
            if (setSelectedTextId) setSelectedTextId(null);
          }
        }}
        disabled={isUndoDisabled || isMultiSelectMode}
        style={{
          opacity: isUndoDisabled || isMultiSelectMode ? 0.4 : 1,
          cursor: isUndoDisabled || isMultiSelectMode ? "not-allowed" : "pointer",
        }}
      >
        {/* <img
          src="./undo.svg"
          alt="Undo"
          style={{ width: "14px", height: "14px" }}
        /> */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 18 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.6148 15.6952V14.0462H11.5041C12.6514 14.0462 13.6386 13.6675 14.4659 12.9099C15.2932 12.1525 15.7069 11.2171 15.7069 10.1037C15.7069 8.99027 15.2932 8.05651 14.4659 7.3024C13.6386 6.54848 12.6514 6.17151 11.5041 6.17151H3.78602L6.84478 9.23055L5.6864 10.3889L0.644531 5.34705L5.6864 0.305176L6.84478 1.46355L3.78602 4.52259H11.5041C13.1122 4.52259 14.4895 5.05922 15.636 6.13249C16.7826 7.20576 17.3559 8.52948 17.3559 10.1037C17.3559 11.6778 16.7826 13.0033 15.636 14.0801C14.4895 15.1568 13.1122 15.6952 11.5041 15.6952H3.6148Z"
            fill="#6C757D"
          />
        </svg>

        <span className="tooltip">Undo (Ctrl Z)</span>
      </button>
      {/* Redo button */}
      <button
        className="toolbar-btn"
        onClick={() => {
          if (!isRedoDisabled && !isMultiSelectMode) {
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
            handleRedo();
            setShowExportMenu(false);
            if (setShowTextBox) setShowTextBox(false);
            if (setSelectedText) setSelectedText(null);
            if (setSelectedTextId) setSelectedTextId(null);
          }
        }}
        disabled={isRedoDisabled || isMultiSelectMode}
        style={{
          opacity: isRedoDisabled || isMultiSelectMode ? 0.4 : 1,
          cursor: isRedoDisabled || isMultiSelectMode ? "not-allowed" : "pointer",
        }}
      >
        {/* <img
          src="./redo.svg"
          alt="Redo"
          style={{ width: "14px", height: "14px", transform: "scaleX(-1)" }}
        /> */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 18 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.4963 15.6952C4.88823 15.6952 3.51092 15.1568 2.36436 14.0801C1.21781 13.0033 0.644531 11.6778 0.644531 10.1037C0.644531 8.52948 1.21781 7.20576 2.36436 6.13249C3.51092 5.05922 4.88823 4.52259 6.4963 4.52259H14.2144L11.1556 1.46355L12.314 0.305176L17.3559 5.34705L12.314 10.3889L11.1556 9.23055L14.2144 6.17151H6.4963C5.34902 6.17151 4.36177 6.54848 3.53455 7.3024C2.70716 8.05651 2.29346 8.99027 2.29346 10.1037C2.29346 11.2171 2.70716 12.1525 3.53455 12.9099C4.36177 13.6675 5.34902 14.0462 6.4963 14.0462H14.3856V15.6952H6.4963Z"
            fill="#6C757D"
          />
        </svg>

        <span className="tooltip">Redo (Ctrl Shift Z or Ctrl Y)</span>
      </button>
      {/* First divider */}
      <div className="toolbar-divider"></div>
      {/* Text button */}
      <button
        className={`toolbar-btn ${action === ACTIONS.TEXT ? "active-tool" : ""
          }`}
        onClick={() => {
          if (isMultiSelectMode) return; // Disable during multi-select
          setLockedAction(null);
          setAction(ACTIONS.TEXT);
          setShowExportMenu(false);
          // Text will be created when user draws on canvas (like rectangles/circles)
        }}
        disabled={isMultiSelectMode}
        style={{
          opacity: isMultiSelectMode ? 0.4 : 1,
          cursor: isMultiSelectMode ? "not-allowed" : "pointer",
        }}
      >
        {/* <img
          src="./tb.svg"
          alt="Text"
          className="toolbar-icon"
          width="16"
          height="16"
        /> */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 20 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect y="0.5" width="20" height="20" rx="2.7" fill="#6C757D" />
          <path
            d="M6.87695 6.46875V15H5.41797V6.46875H6.87695ZM9.55469 6.46875V7.64062H2.76367V6.46875H9.55469ZM13.8789 11.1445H11.7051L11.6934 10.0723H13.5918C13.9121 10.0723 14.1836 10.0254 14.4062 9.93164C14.6328 9.83398 14.8047 9.69531 14.9219 9.51562C15.0391 9.33203 15.0977 9.11133 15.0977 8.85352C15.0977 8.56836 15.043 8.33594 14.9336 8.15625C14.8242 7.97656 14.6562 7.8457 14.4297 7.76367C14.207 7.68164 13.9219 7.64062 13.5742 7.64062H12.1504V15H10.6797V6.46875H13.5742C14.043 6.46875 14.4609 6.51367 14.8281 6.60352C15.1992 6.69336 15.5137 6.83398 15.7715 7.02539C16.0332 7.21289 16.2305 7.45117 16.3633 7.74023C16.5 8.0293 16.5684 8.37305 16.5684 8.77148C16.5684 9.12305 16.4844 9.44531 16.3164 9.73828C16.1484 10.0273 15.9004 10.2637 15.5723 10.4473C15.2441 10.6309 14.8359 10.7402 14.3477 10.7754L13.8789 11.1445ZM13.8145 15H11.2422L11.9043 13.834H13.8145C14.1465 13.834 14.4238 13.7793 14.6465 13.6699C14.8691 13.5566 15.0352 13.4023 15.1445 13.207C15.2578 13.0078 15.3145 12.7754 15.3145 12.5098C15.3145 12.2324 15.2656 11.9922 15.168 11.7891C15.0703 11.582 14.916 11.4238 14.7051 11.3145C14.4941 11.2012 14.2188 11.1445 13.8789 11.1445H12.2266L12.2383 10.0723H14.3887L14.7227 10.4766C15.1914 10.4922 15.5762 10.5957 15.877 10.7871C16.1816 10.9785 16.4082 11.2266 16.5566 11.5312C16.7051 11.8359 16.7793 12.1641 16.7793 12.5156C16.7793 13.0586 16.6602 13.5137 16.4219 13.8809C16.1875 14.248 15.8496 14.5273 15.4082 14.7188C14.9668 14.9062 14.4355 15 13.8145 15Z"
            fill="white"
          />
        </svg>
        <span className="tooltip">Text (T)</span>
      </button>
      {/* Rectangle button */}
      <button
        className={`toolbar-btn ${action === ACTIONS.RECTANGLE ? "active-tool" : ""
          } ${lockedAction === ACTIONS.RECTANGLE ? "locked-tool" : ""}`}
        onClick={() => {
          if (isMultiSelectMode) return; // Disable during multi-select
          // Single click: if tool is locked, unlock it; otherwise select tool for one-time creation
          if (lockedAction === ACTIONS.RECTANGLE) {
            // If tool is locked, unlock it on single click
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
            setShowExportMenu(false);
            if (setShowTextBox) setShowTextBox(false);
            if (setSelectedText) setSelectedText(null);
            if (setSelectedTextId) setSelectedTextId(null);
            return;
          }
          setLockedAction(null);
          setAction(ACTIONS.RECTANGLE);
          setShowExportMenu(false);
          if (setShowTextBox) setShowTextBox(false);
          if (setSelectedText) setSelectedText(null);
          if (setSelectedTextId) setSelectedTextId(null);
        }}
        onDoubleClick={() => {
          if (isMultiSelectMode) return; // Disable during multi-select
          // Double click: lock/unlock tool
          if (lockedAction === ACTIONS.RECTANGLE) {
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
          } else {
            setLockedAction(ACTIONS.RECTANGLE);
            setAction(ACTIONS.RECTANGLE);
          }
          setShowExportMenu(false);
          if (setShowTextBox) setShowTextBox(false);
          if (setSelectedText) setSelectedText(null);
          if (setSelectedTextId) setSelectedTextId(null);
        }}
        disabled={isMultiSelectMode}
        style={{
          opacity: isMultiSelectMode ? 0.4 : 1,
          cursor: isMultiSelectMode ? "not-allowed" : "pointer",
        }}
      >
        {/* <img
          src="./rectangle.svg"
          alt="Rectangle"
          className="toolbar-icon"
          width="16"
          height="16"
        /> */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 20 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="1.355"
            y="1.4424"
            width="17.29"
            height="11.115"
            stroke={lockedAction === ACTIONS.RECTANGLE ? "white" : "#6C757D"}
            strokeWidth="1.71"
          />
        </svg>
        {lockedAction === ACTIONS.RECTANGLE && (
          <svg
            className="lock-icon"
            width="9"
            height="10"
            viewBox="0 0 11 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g filter="url(#filter0_d_1991_8814_rect)">
              <path
                d="M2.77778 10C2.56389 10 2.38079 9.9254 2.22847 9.77619C2.07616 9.62698 2 9.44762 2 9.2381V5.42857C2 5.21905 2.07616 5.03968 2.22847 4.89048C2.38079 4.74127 2.56389 4.66667 2.77778 4.66667H3.16667V3.90476C3.16667 3.37778 3.35625 2.92857 3.73542 2.55714C4.11458 2.18571 4.57315 2 5.11111 2C5.64907 2 6.10764 2.18571 6.48681 2.55714C6.86597 2.92857 7.05556 3.37778 7.05556 3.90476V4.66667H7.44444C7.65833 4.66667 7.84144 4.74127 7.99375 4.89048C8.14606 5.03968 8.22222 5.21905 8.22222 5.42857V9.2381C8.22222 9.44762 8.14606 9.62698 7.99375 9.77619C7.84144 9.9254 7.65833 10 7.44444 10H2.77778ZM5.11111 8.09524C5.325 8.09524 5.5081 8.02063 5.66042 7.87143C5.81273 7.72222 5.88889 7.54286 5.88889 7.33333C5.88889 7.12381 5.81273 6.94444 5.66042 6.79524C5.5081 6.64603 5.325 6.57143 5.11111 6.57143C4.89722 6.57143 4.71412 6.64603 4.56181 6.79524C4.40949 6.94444 4.33333 7.12381 4.33333 7.33333C4.33333 7.54286 4.40949 7.72222 4.56181 7.87143C4.71412 8.02063 4.89722 8.09524 5.11111 8.09524ZM3.94444 4.66667H6.27778V3.90476C6.27778 3.5873 6.16435 3.31746 5.9375 3.09524C5.71065 2.87302 5.43519 2.7619 5.11111 2.7619C4.78704 2.7619 4.51157 2.87302 4.28472 3.09524C4.05787 3.31746 3.94444 3.5873 3.94444 3.90476V4.66667Z"
                fill="white"
              />
            </g>
            <defs>
              <filter
                id="filter0_d_1991_8814_rect"
                x="0"
                y="0"
                width="10.2188"
                height="12"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset />
                <feGaussianBlur stdDeviation="1" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                />
                <feBlend
                  mode="normal"
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_1991_8814"
                />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect1_dropShadow_1991_8814"
                  result="shape"
                />
              </filter>
            </defs>
          </svg>
        )}
        <span className="tooltip">Rectangle (R)</span>
      </button>
      {/* Circle button */}
      <button
        className={`toolbar-btn ${action === ACTIONS.CIRCLE ? "active-tool" : ""
          } ${lockedAction === ACTIONS.CIRCLE ? "locked-tool" : ""}`}
        onClick={() => {
          if (isMultiSelectMode) return; // Disable during multi-select
          // Single click: if tool is locked, unlock it; otherwise select tool for one-time creation
          if (lockedAction === ACTIONS.CIRCLE) {
            // If tool is locked, unlock it on single click
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
            setShowExportMenu(false);
            if (setShowTextBox) setShowTextBox(false);
            if (setSelectedText) setSelectedText(null);
            if (setSelectedTextId) setSelectedTextId(null);
            return;
          }
          setLockedAction(null);
          setAction(ACTIONS.CIRCLE);
          setShowExportMenu(false);
          if (setShowTextBox) setShowTextBox(false);
          if (setSelectedText) setSelectedText(null);
          if (setSelectedTextId) setSelectedTextId(null);
        }}
        onDoubleClick={() => {
          if (isMultiSelectMode) return; // Disable during multi-select
          // Double click: lock/unlock tool
          if (lockedAction === ACTIONS.CIRCLE) {
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
          } else {
            setLockedAction(ACTIONS.CIRCLE);
            setAction(ACTIONS.CIRCLE);
          }
          setShowExportMenu(false);
          if (setShowTextBox) setShowTextBox(false);
          if (setSelectedText) setSelectedText(null);
          if (setSelectedTextId) setSelectedTextId(null);
        }}
        disabled={isMultiSelectMode}
        style={{
          opacity: isMultiSelectMode ? 0.4 : 1,
          cursor: isMultiSelectMode ? "not-allowed" : "pointer",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="10"
            cy="10"
            r="8.5"
            stroke={lockedAction === ACTIONS.CIRCLE ? "white" : "#6C757D"}
            strokeWidth="1.71"
          />
        </svg>
        {lockedAction === ACTIONS.CIRCLE && (
          <svg
            className="lock-icon"
            width="9"
            height="10"
            viewBox="0 0 11 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g filter="url(#filter0_d_1991_8814_circle)">
              <path
                d="M2.77778 10C2.56389 10 2.38079 9.9254 2.22847 9.77619C2.07616 9.62698 2 9.44762 2 9.2381V5.42857C2 5.21905 2.07616 5.03968 2.22847 4.89048C2.38079 4.74127 2.56389 4.66667 2.77778 4.66667H3.16667V3.90476C3.16667 3.37778 3.35625 2.92857 3.73542 2.55714C4.11458 2.18571 4.57315 2 5.11111 2C5.64907 2 6.10764 2.18571 6.48681 2.55714C6.86597 2.92857 7.05556 3.37778 7.05556 3.90476V4.66667H7.44444C7.65833 4.66667 7.84144 4.74127 7.99375 4.89048C8.14606 5.03968 8.22222 5.21905 8.22222 5.42857V9.2381C8.22222 9.44762 8.14606 9.62698 7.99375 9.77619C7.84144 9.9254 7.65833 10 7.44444 10H2.77778ZM5.11111 8.09524C5.325 8.09524 5.5081 8.02063 5.66042 7.87143C5.81273 7.72222 5.88889 7.54286 5.88889 7.33333C5.88889 7.12381 5.81273 6.94444 5.66042 6.79524C5.5081 6.64603 5.325 6.57143 5.11111 6.57143C4.89722 6.57143 4.71412 6.64603 4.56181 6.79524C4.40949 6.94444 4.33333 7.12381 4.33333 7.33333C4.33333 7.54286 4.40949 7.72222 4.56181 7.87143C4.71412 8.02063 4.89722 8.09524 5.11111 8.09524ZM3.94444 4.66667H6.27778V3.90476C6.27778 3.5873 6.16435 3.31746 5.9375 3.09524C5.71065 2.87302 5.43519 2.7619 5.11111 2.7619C4.78704 2.7619 4.51157 2.87302 4.28472 3.09524C4.05787 3.31746 3.94444 3.5873 3.94444 3.90476V4.66667Z"
                fill="white"
              />
            </g>
            <defs>
              <filter
                id="filter0_d_1991_8814_circle"
                x="0"
                y="0"
                width="10.2188"
                height="12"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset />
                <feGaussianBlur stdDeviation="1" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                />
                <feBlend
                  mode="normal"
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_1991_8814"
                />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect1_dropShadow_1991_8814"
                  result="shape"
                />
              </filter>
            </defs>
          </svg>
        )}
        <span className="tooltip">Circle (C)</span>
      </button>
      {/* Line button */}
      <button
        className={`toolbar-btn ${action === ACTIONS.LINE ? "active-tool" : ""
          } ${lockedAction === ACTIONS.LINE ? "locked-tool" : ""}`}
        onClick={() => {
          if (isMultiSelectMode) return; // Disable during multi-select
          // Single click: if tool is locked, unlock it; otherwise select tool for one-time creation
          if (lockedAction === ACTIONS.LINE) {
            // If tool is locked, unlock it on single click
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
            setShowExportMenu(false);
            if (setShowTextBox) setShowTextBox(false);
            if (setSelectedText) setSelectedText(null);
            if (setSelectedTextId) setSelectedTextId(null);
            return;
          }
          setLockedAction(null);
          setAction(ACTIONS.LINE);
          setShowExportMenu(false);
          if (setShowTextBox) setShowTextBox(false);
          if (setSelectedText) setSelectedText(null);
          if (setSelectedTextId) setSelectedTextId(null);
        }}
        onDoubleClick={() => {
          if (isMultiSelectMode) return; // Disable during multi-select
          // Double click: lock/unlock tool
          if (lockedAction === ACTIONS.LINE) {
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
          } else {
            setLockedAction(ACTIONS.LINE);
            setAction(ACTIONS.LINE);
          }
          setShowExportMenu(false);
          if (setShowTextBox) setShowTextBox(false);
          if (setSelectedText) setSelectedText(null);
          if (setSelectedTextId) setSelectedTextId(null);
        }}
        disabled={isMultiSelectMode}
        style={{
          opacity: isMultiSelectMode ? 0.4 : 1,
          cursor: isMultiSelectMode ? "not-allowed" : "pointer",
        }}
      >
        {/* <img
          src="./line.svg"
          alt="Line"
          className="toolbar-icon"
          width="16"
          height="16"
        /> */}
        {isDefaultDoubleHeadLine ? (
          <svg
            width="20"
            height="13"
            viewBox="0 0 20 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 12.825L7.39648 12.4801L3.39952 6.247L0 12.825ZM20 0L12.6035 0.344931L16.6005 6.578L20 0ZM4.8582 9.70968L5.20435 10.2495L15.4879 3.65512L15.1418 3.11532L14.7957 2.57552L4.51205 9.16988L4.8582 9.70968Z"
              fill={lineToolIconColor}
            />
          </svg>
        ) : isDefaultNoHeadLine ? (
          <svg
            width="21"
            height="15"
            viewBox="0 0 21 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.71875 13.888L19.7187 1.06299"
              stroke={lineToolIconColor}
              strokeWidth="2.565"
            />
          </svg>
        ) : (
          <svg
            width="21"
            height="14"
            viewBox="0 0 21 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.6953 0L5.90236 0.689861L13.8963 13.156L20.6953 0ZM0.695312 12.825L1.38761 13.9046L11.6712 7.31024L10.9789 6.23064L10.2866 5.15104L0.00301909 11.7454L0.695312 12.825Z"
              fill={lineToolIconColor}
            />
          </svg>
        )}
        {lockedAction === ACTIONS.LINE && (
          <svg
            className="lock-icon"
            width="9"
            height="10"
            viewBox="0 0 11 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g filter="url(#filter0_d_1991_8814_line)">
              <path
                d="M2.77778 10C2.56389 10 2.38079 9.9254 2.22847 9.77619C2.07616 9.62698 2 9.44762 2 9.2381V5.42857C2 5.21905 2.07616 5.03968 2.22847 4.89048C2.38079 4.74127 2.56389 4.66667 2.77778 4.66667H3.16667V3.90476C3.16667 3.37778 3.35625 2.92857 3.73542 2.55714C4.11458 2.18571 4.57315 2 5.11111 2C5.64907 2 6.10764 2.18571 6.48681 2.55714C6.86597 2.92857 7.05556 3.37778 7.05556 3.90476V4.66667H7.44444C7.65833 4.66667 7.84144 4.74127 7.99375 4.89048C8.14606 5.03968 8.22222 5.21905 8.22222 5.42857V9.2381C8.22222 9.44762 8.14606 9.62698 7.99375 9.77619C7.84144 9.9254 7.65833 10 7.44444 10H2.77778ZM5.11111 8.09524C5.325 8.09524 5.5081 8.02063 5.66042 7.87143C5.81273 7.72222 5.88889 7.54286 5.88889 7.33333C5.88889 7.12381 5.81273 6.94444 5.66042 6.79524C5.5081 6.64603 5.325 6.57143 5.11111 6.57143C4.89722 6.57143 4.71412 6.64603 4.56181 6.79524C4.40949 6.94444 4.33333 7.12381 4.33333 7.33333C4.33333 7.54286 4.40949 7.72222 4.56181 7.87143C4.71412 8.02063 4.89722 8.09524 5.11111 8.09524ZM3.94444 4.66667H6.27778V3.90476C6.27778 3.5873 6.16435 3.31746 5.9375 3.09524C5.71065 2.87302 5.43519 2.7619 5.11111 2.7619C4.78704 2.7619 4.51157 2.87302 4.28472 3.09524C4.05787 3.31746 3.94444 3.5873 3.94444 3.90476V4.66667Z"
                fill="white"
              />
            </g>
            <defs>
              <filter
                id="filter0_d_1991_8814_line"
                x="0"
                y="0"
                width="10.2188"
                height="12"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset />
                <feGaussianBlur stdDeviation="1" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                />
                <feBlend
                  mode="normal"
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_1991_8814"
                />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect1_dropShadow_1991_8814"
                  result="shape"
                />
              </filter>
            </defs>
          </svg>
        )}
        <span className="tooltip">Arrow (A/L)</span>
      </button>
      {/* Second divider */}
      <div className="toolbar-divider"></div>
      {/* Import Canvas button — label is focusable; file input stays off tab order (display:none) */}
      <label
        htmlFor="importCanvas"
        className="toolbar-btn"
        tabIndex={isMultiSelectMode ? -1 : 0}
        onKeyDown={(e) => {
          if (isMultiSelectMode) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            importCanvasInputRef.current?.click();
          }
        }}
        style={{
          opacity: isMultiSelectMode ? 0.4 : 1,
          cursor: isMultiSelectMode ? "not-allowed" : "pointer",
          pointerEvents: isMultiSelectMode ? "none" : "auto",
        }}
      >
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.97852 16.9114C1.52972 16.9114 1.14632 16.7525 0.828316 16.4346C0.510467 16.1166 0.351562 15.7332 0.351562 15.2844V13.6614H1.70156V15.2844C1.70156 15.3537 1.73044 15.4172 1.78819 15.4748C1.84578 15.5325 1.90923 15.5614 1.97852 15.5614H14.0245C14.0938 15.5614 14.1572 15.5325 14.2148 15.4748C14.2726 15.4172 14.3015 15.3537 14.3015 15.2844V13.6614H15.6515V15.2844C15.6515 15.7332 15.4926 16.1166 15.1748 16.4346C14.8568 16.7525 14.4734 16.9114 14.0245 16.9114H1.97852ZM7.28152 6.2344L4.88819 8.62774L5.84069 9.58024L7.19402 8.2269V15.0304H8.80836V8.2269L10.1617 9.58024L11.1142 8.62774L8.72086 6.2344C8.56394 6.07748 8.38652 5.99902 8.18861 5.99902C7.99069 5.99902 7.81327 6.07748 7.65636 6.2344H7.28152Z"
            fill="#6C757D"
          />
        </svg>

        <input
          ref={importCanvasInputRef}
          type="file"
          accept=".json,.cjson,application/json"
          id="importCanvas"
          className="hidden-input"
          tabIndex={-1}
          onChange={handleImportCanvas}
          onClick={() => {
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
            setShowExportMenu(false);
            if (setShowTextBox) setShowTextBox(false);
          }}
        />

        <span className="tooltip">Import Canvas</span>
      </label>
      {/* Export Canvas button */}
      <div className="toolbar-btn">
        <button
          ref={exportButtonRef}
          className="toolbar-btn"
          type="button"
          onClick={(e) => {
            if (isMultiSelectMode) return; // Disable during multi-select
            setLockedAction(null);
            const rect = exportButtonRef.current.getBoundingClientRect();
            setExportMenuPosition({
              top: rect.bottom + window.scrollY,
              left: rect.left + window.scrollX,
            });
            setShowExportMenu(true);
            setAction(ACTIONS.SELECT);
            if (setShowTextBox) setShowTextBox(false);
          }}
          disabled={isMultiSelectMode}
          style={{
            opacity: isMultiSelectMode ? 0.4 : 1,
            cursor: isMultiSelectMode ? "not-allowed" : "pointer",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              fill="#6C757D"
              d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16a1 1 0 001-1v-4h-2v3H5v-3H3v4a1 1 0 001 1z"
              stroke="#6C757D"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <span className="tooltip">Export Canvas</span>
        </button>
      </div>
      {/* Image upload button */}
      <label
        htmlFor="imageUpload"
        className="toolbar-btn"
        tabIndex={isMultiSelectMode ? -1 : 0}
        onKeyDown={(e) => {
          if (isMultiSelectMode) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            imageUploadInputRef.current?.click();
          }
        }}
        style={{
          opacity: isMultiSelectMode ? 0.4 : 1,
          cursor: isMultiSelectMode ? "not-allowed" : "pointer",
          pointerEvents: isMultiSelectMode ? "none" : "auto",
        }}
      >
        {/* <img
          src="./add_image.svg"
          alt="Upload Image"
          className="toolbar-icon"
          width="16"
          height="16"
        /> */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.62112 17.0999C2.13972 17.0999 1.73226 16.9331 1.39873 16.5996C1.0652 16.2661 0.898438 15.8586 0.898438 15.3772V2.62258C0.898438 2.14119 1.0652 1.73373 1.39873 1.4002C1.73226 1.06667 2.13972 0.899902 2.62112 0.899902H9.76818V2.32931H2.62112C2.53551 2.32931 2.46523 2.35679 2.41028 2.41174C2.35533 2.4667 2.32785 2.53698 2.32785 2.62258V15.3772C2.32785 15.4628 2.35533 15.5331 2.41028 15.5881C2.46523 15.643 2.53551 15.6705 2.62112 15.6705H15.3758C15.4614 15.6705 15.5316 15.643 15.5866 15.5881C15.6416 15.5331 15.669 15.4628 15.669 15.3772V8.23017H17.0984V15.3772C17.0984 15.8586 16.9317 16.2661 16.5981 16.5996C16.2646 16.9331 15.8572 17.0999 15.3758 17.0999H2.62112ZM3.64743 13.4713H14.3494L11.0234 9.03659L8.16461 12.7476L6.13961 10.1727L3.64743 13.4713ZM13.7631 6.14108V4.2352H11.8573V2.80578H13.7631V0.899902H15.1926V2.80578H17.0984V4.2352H15.1926V6.14108H13.7631Z"
            fill="#6C757D"
          />
        </svg>

        <input
          ref={imageUploadInputRef}
          type="file"
          accept={getSupportedImageAcceptString()}
          id="imageUpload"
          className="hidden-input"
          tabIndex={-1}
          onChange={handleImageUpload}
          onClick={() => {
            setLockedAction(null);
            setShowExportMenu(false);
            setAction(ACTIONS.SELECT);
            if (setShowTextBox) setShowTextBox(false);
          }}
        />
        <span className="tooltip">Upload Image (I)</span>
      </label>
      {/* Camera button */}
      <button
        className="toolbar-btn"
        onClick={() => {
          if (isMultiSelectMode) return; // Disable during multi-select
          setLockedAction(null);
          // Close ShapeEditor by clearing selected objects
          if (setSelectedObject) setSelectedObject(null);
          if (setSelectedObjects) setSelectedObjects([]);
          // Close TextBoxWithButtons by clearing selected text
          if (setShowTextBox) setShowTextBox(false);
          if (setSelectedText) setSelectedText(null);
          if (setSelectedTextId) setSelectedTextId(null);
          // Check if camera is supported before opening popup
          if (!navigator.mediaDevices?.getUserMedia) {
            alert(
              "Camera is not supported in this browser. Please use the 'Upload Image' option instead."
            );
            return;
          }
          setShowWebcamPopup(true);
          setAction(ACTIONS.SELECT);
          setShowExportMenu(false);
        }}
        disabled={isMultiSelectMode}
        style={{
          opacity: isMultiSelectMode ? 0.4 : 1,
          cursor: isMultiSelectMode ? "not-allowed" : "pointer",
        }}
      >
        {/* <img
          src="./camera.svg"
          alt="Camera"
          className="toolbar-icon"
          width="16"
          height="16"
        /> */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.5495 17.1426C1.1165 17.1426 0.75 16.9926 0.45 16.6926C0.15 16.3926 0 16.0261 0 15.5931V5.835C0 5.402 0.15 5.0355 0.45 4.7355C0.75 4.4355 1.1165 4.2855 1.5495 4.2855H4.167L5.75271 2.57122H10.5495V3.85693H6.31329L4.74064 5.57122H1.5495C1.4725 5.57122 1.40929 5.59593 1.35986 5.64536C1.31043 5.69479 1.28571 5.75801 1.28571 5.835V15.5931C1.28571 15.6701 1.31043 15.7334 1.35986 15.7828C1.40929 15.8322 1.4725 15.8569 1.5495 15.8569H14.7362C14.8132 15.8569 14.8764 15.8322 14.9259 15.7828C14.9753 15.7334 15 15.6701 15 15.5931V8.30743H16.2857V15.5931C16.2857 16.0261 16.1357 16.3926 15.8357 16.6926C15.5357 16.9926 15.1692 17.1426 14.7362 17.1426H1.5495ZM15 5.57122V3.85693H13.2857V2.57122H15V0.856934H16.2857V2.57122H18V3.85693H16.2857V5.57122H15ZM8.14286 14.2414C9.12643 14.2414 9.96021 13.8994 10.6442 13.2154C11.3282 12.5314 11.6702 11.6976 11.6702 10.7141C11.6702 9.7305 11.3282 8.89672 10.6442 8.21272C9.96021 7.52872 9.12643 7.18672 8.14286 7.18672C7.15929 7.18672 6.3255 7.52872 5.6415 8.21272C4.9575 8.89672 4.6155 9.7305 4.6155 10.7141C4.6155 11.6976 4.9575 12.5314 5.6415 13.2154C6.3255 13.8994 7.15929 14.2414 8.14286 14.2414ZM8.14286 12.9559C7.50986 12.9559 6.978 12.7405 6.54729 12.3096C6.11643 11.8789 5.901 11.3471 5.901 10.7141C5.901 10.0811 6.11643 9.54922 6.54729 9.1185C6.978 8.68765 7.50986 8.47222 8.14286 8.47222C8.77586 8.47222 9.30772 8.68765 9.73843 9.1185C10.1693 9.54922 10.3847 10.0811 10.3847 10.7141C10.3847 11.3471 10.1693 11.8789 9.73843 12.3096C9.30772 12.7405 8.77586 12.9559 8.14286 12.9559Z"
            fill="#6C757D"
          />
        </svg>

        <span className="tooltip">Camera (P)</span>
      </button>
      <input
        type="text"
        id="clipboard-fallback"
        style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
        tabIndex={-1}
        readOnly
      />
      <div className="toolbar-btn">
        <button
          className="toolbar-btn"
          type="button"
          onClick={() => {
            if (isMultiSelectMode || isPastingFromClipboard) return;
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
            handlePaste();
            setShowExportMenu(false);
            if (setShowTextBox) setShowTextBox(false);
          }}
          disabled={isMultiSelectMode || isPastingFromClipboard}
          style={{
            opacity: isMultiSelectMode || isPastingFromClipboard ? 0.4 : 1,
            cursor:
              isMultiSelectMode || isPastingFromClipboard
                ? "not-allowed"
                : "pointer",
          }}
        >
          <svg
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.97854 16.9114C1.52974 16.9114 1.14634 16.7525 0.828338 16.4346C0.510488 16.1166 0.351562 15.7332 0.351562 15.2844V3.23838C0.351562 2.78958 0.510488 2.40618 0.828338 2.08818C1.14634 1.77033 1.52974 1.6114 1.97854 1.6114H5.94371C6.06836 1.17295 6.32306 0.809504 6.70781 0.521054C7.09256 0.232603 7.52381 0.0883789 8.00156 0.0883789C8.49776 0.0883789 8.93591 0.232603 9.31601 0.521054C9.69626 0.809504 9.94871 1.17295 10.0734 1.6114H14.0246C14.4734 1.6114 14.8568 1.77033 15.1748 2.08818C15.4926 2.40618 15.6516 2.78958 15.6516 3.23838V15.2844C15.6516 15.7332 15.4926 16.1166 15.1748 16.4346C14.8568 16.7525 14.4734 16.9114 14.0246 16.9114H1.97854ZM1.97854 15.5614H14.0246C14.0939 15.5614 14.1573 15.5325 14.2149 15.4748C14.2727 15.4172 14.3016 15.3537 14.3016 15.2844V3.23838C14.3016 3.16908 14.2727 3.10563 14.2149 3.04803C14.1573 2.99028 14.0939 2.9614 14.0246 2.9614H12.0516V5.31513H3.95156V2.9614H1.97854C1.90924 2.9614 1.84579 2.99028 1.78819 3.04803C1.73044 3.10563 1.70156 3.16908 1.70156 3.23838V15.2844C1.70156 15.3537 1.73044 15.4172 1.78819 15.4748C1.84579 15.5325 1.90924 15.5614 1.97854 15.5614ZM8.00156 3.06535C8.23346 3.06535 8.42704 2.98773 8.58229 2.83248C8.73739 2.67723 8.81494 2.48365 8.81494 2.25175C8.81494 2.01985 8.73739 1.82628 8.58229 1.67103C8.42704 1.51593 8.23346 1.43838 8.00156 1.43838C7.76966 1.43838 7.57609 1.51593 7.42084 1.67103C7.26574 1.82628 7.18819 2.01985 7.18819 2.25175C7.18819 2.48365 7.26574 2.67723 7.42084 2.83248C7.57609 2.98773 7.76966 3.06535 8.00156 3.06535Z"
              fill="#6C757D"
            />
          </svg>

          <span className="tooltip">Paste from Clipboard (Ctrl V)</span>
        </button>
      </div>

      {showExportMenu && (
        <div
          ref={exportMenuRef}
          className="export-format-menu"
          style={{
            position: "absolute",
            top: exportMenuPosition.top,
            left: exportMenuPosition.left,
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            padding: "24px 8px 8px 8px", // leave space at top for ✕
            zIndex: 1000,
            width: "60px",
            marginLeft: "15px",
            marginTop: "-105px",
            pointerEvents: "auto", // Enable pointer events for the menu
          }}
        >
          {/* Close Icon Button */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              setShowExportMenu(false);
              if (setShowTextBox) setShowTextBox(false);
            }}
            style={{
              position: "absolute",
              top: "4px",
              // left: "4px",
              marginLeft: "40px",
              marginTop: "-10px",
              background: "none",
              border: "none",
              fontSize: "14px",
              cursor: "pointer",
              color: "#888",
              pointerEvents: "auto", // Ensure button is clickable
              zIndex: 1001, // Ensure it's above the menu
            }}
            aria-label="Close Export Menu"
          >
            ✕
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              handleExport("png");
              setShowExportMenu(false);
              if (setShowTextBox) setShowTextBox(false);
            }}
            style={{
              pointerEvents: "auto", // Ensure button is clickable
              cursor: "pointer",
            }}
          >
            PNG
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              handleExport("svg");
              setShowExportMenu(false);
              if (setShowTextBox) setShowTextBox(false);
            }}
            style={{
              pointerEvents: "auto", // Ensure button is clickable
              cursor: "pointer",
            }}
          >
            SVG
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              handleExport("pdf");
              setShowExportMenu(false);
              if (setShowTextBox) setShowTextBox(false);
            }}
            style={{
              pointerEvents: "auto", // Ensure button is clickable
              cursor: "pointer",
            }}
          >
            PDF
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              handleExport("cjson");
              setShowExportMenu(false);
              if (setShowTextBox) setShowTextBox(false);
            }}
            style={{
              pointerEvents: "auto", // Ensure button is clickable
              cursor: "pointer",
            }}
          >
            CJSON
          </button>
        </div>
      )}
    </div>
  );
};

Toolbar.propTypes = {
  action: PropTypes.string.isRequired,
  setAction: PropTypes.func.isRequired,
  ACTIONS: PropTypes.object.isRequired,
  addText: PropTypes.func.isRequired,
  deleteSelectedObject: PropTypes.func.isRequired,
  fillColor: PropTypes.string.isRequired,
  setFillColor: PropTypes.func.isRequired,
  handleExport: PropTypes.func.isRequired,
  handleImageUpload: PropTypes.func.isRequired,
  handleImportCanvas: PropTypes.func.isRequired,
  setShowWebcamPopup: PropTypes.func.isRequired,
  handleUndo: PropTypes.func,
  handleRedo: PropTypes.func,
  clearCanvas: PropTypes.func,
  history: PropTypes.array.isRequired,
  redoStack: PropTypes.array.isRequired,
  zoomLevel: PropTypes.number,
  setShowTextBox: PropTypes.func,
  setSelectedText: PropTypes.func,
  setSelectedTextId: PropTypes.func,
  lockedAction: PropTypes.string,
  setLockedAction: PropTypes.func,
  setSelectedObject: PropTypes.func,
  setSelectedObjects: PropTypes.func,
  isMultiSelectMode: PropTypes.bool,
  isPastingFromClipboard: PropTypes.bool,
};

export default Toolbar;
